import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  BottomPanelKey,
  EventLeaveReason,
  EventType,
  InviteeSchema,
  Layout,
  LiveEventAction,
  LiveEventTokenSource,
  OnStageUser,
  StreamingPlatform,
  UserActionType,
  UserRole
} from 'types';
import {
  generateStreamingEndpoint,
  getCallbackTriggersMap,
  getChannelURL,
  getDuration,
  getEventURL,
  hasFullSupport,
  MAX_STAGE_SEATS,
  MAX_VIEWER_COUNT_UPDATE_INTERVAL,
  PREVENT_ENDING_EVENT_IN,
  SHOW_BECOME_MEMBER_MODAL_INTERVAL,
  UZBEKITTV_CHANNEL_NAME
} from 'utils';
import { eventsActions, streamingPlatformsActions } from 'store/actions';
import {
  activeStreamingPlatformsSelector,
  authSelector,
  callbackTriggersSelector,
  eventNameSelector,
  eventSessionSelector,
  filledSeatsCountSelector,
  isAnonymousViewerSelector,
  isHandRaiseEnabledSelector,
  isHostSelector,
  isInvitedToStageSelector,
  isInviteeSelector,
  isMemberSelector,
  isModeratorSelector,
  isRecordedSelector,
  isSpeakerSelector,
  isViewerSelector,
  metaSelector,
  userSelector
} from 'store/selectors';
import { callbackTriggersActions } from 'store/slices/callbackTriggers';
import { FacebookStreamingPlatformState } from 'store/slices/streamingPlatforms';
import { api, LoggerService, tracker } from 'services';
import {
  useBottomSheet,
  useConfirmation,
  useLiveEventActions,
  useModal,
  useRoom,
  useRouter,
  useStore,
  useToast
} from 'hooks';
import { BecomeMemberModal, BottomPanels, ConfirmPanel, Guests, HandRaises, InviteToStage } from 'containers';
import { ControlPanel, Loader, PermissionModal, Unsupported } from 'components';
import { RaiseHandIcon } from 'components/Icon';

import { LiveEventContext, liveEventContext } from './LiveEvent.context';
import { useAutoEndWarning } from './LiveEvent.hooks';
import JoinPanel from './LiveEvent.JoinPanel';
import Login from './LiveEvent.Login';
import MainPlayer from './LiveEvent.MainPlayer';
import OnStage from './LiveEvent.OnStage';
import Pin from './LiveEvent.Pin';
import PlayAudio from './LiveEvent.PlayAudio';
import Playback from './LiveEvent.Playback';
import SharePanel from './LiveEvent.Share';
import TopBar from './LiveEvent.TopBar';

import styles from './LiveEvent.module.scss';

export type BottomSheetContentKey =
  | BottomPanelKey.SHARE
  | BottomPanelKey.STUDIO
  | BottomPanelKey.REQUESTS
  | BottomPanelKey.LOGIN
  | BottomPanelKey.JOIN_THE_STAGE
  | BottomPanelKey.GENERATE_INVITATION_LINK;

export type BottomSheetParams = {
  defaultRole?: UserRole.MODERATOR | UserRole.SPEAKER | UserRole.VIEWER;
  invitee?: InviteeSchema;
  selectedSeatIndex?: number;
  attempt?: LiveEventAction;
};

export type ConfirmationAction = 'END_EVENT' | 'LEAVE_STAGE' | 'LEAVE_EVENT' | 'DECLINE_JOINING';

export type LiveEventProps = Pick<LiveEventContext, 'channelName' | 'eventCode' | 'inviteeCode'> & {
  ingestServer?: string;
  playbackUrl?: string;
  onEventEnded?(): void;
  onAudienceLimitIsReached?(): void;
  onJoinedFromAnotherSession?(): void;
};

export type EventContainerState = Pick<LiveEventContext, 'isPictureInPictureModeEnabled'> & {
  previewMode?: boolean;
  autoplayBlocked: boolean;
  isForceMuteTooltipVisible?: boolean;
  isConnectionStable?: boolean;
  defaultAudioMuted?: boolean;
  defaultVideoMuted?: boolean;
  permissionModal?: boolean;
};

const LiveEvent: FC<LiveEventProps> = ({
  channelName,
  eventCode,
  inviteeCode,
  ingestServer,
  playbackUrl,
  onEventEnded,
  onAudienceLimitIsReached,
  onJoinedFromAnotherSession
}) => {
  const liveEventActions = useLiveEventActions(eventCode);
  const dispatch = useDispatch();
  const eventSession = useStore(eventSessionSelector(eventCode));
  const router = useRouter<
    object,
    {
      from?: 'setup_panel';
      defaultAudioMuted?: boolean;
      defaultVideoMuted?: boolean;
    }
  >();

  const {
    auth,
    eventMeta,
    eventName,
    localUser,
    activeStreamingPlatforms,
    isMember,
    isHost,
    isInvitee,
    isModerator,
    isSpeaker,
    isViewer,
    isAnonymousViewer,
    isInvitedToStage,
    filledSeatsCount,
    isHandRaiseEnabled,
    isRecorded,
    callbackTriggers
  } = useStore(state => ({
    auth: authSelector(state),
    eventMeta: metaSelector(eventCode)(state),
    eventName: eventNameSelector(eventCode)(state),
    localUser: userSelector(eventCode, eventSession.username)(state),
    activeStreamingPlatforms: activeStreamingPlatformsSelector(state),
    isMember: isMemberSelector(channelName)(state),
    isHost: isHostSelector(eventCode, eventSession.username)(state),
    isInvitee: isInviteeSelector(eventCode, eventSession.username)(state),
    isSpeaker: isSpeakerSelector(eventCode, eventSession.username)(state),
    isModerator: isModeratorSelector(eventCode, eventSession.username)(state),
    isViewer: isViewerSelector(eventCode, eventSession.username)(state),
    isAnonymousViewer: isAnonymousViewerSelector(eventCode, eventSession.username)(state),
    isInvitedToStage: isInvitedToStageSelector(eventCode, eventSession.username)(state),
    filledSeatsCount: filledSeatsCountSelector(eventCode)(state),
    isHandRaiseEnabled: isHandRaiseEnabledSelector(eventCode)(state),
    isRecorded: isRecordedSelector(eventCode)(state),
    callbackTriggers: callbackTriggersSelector(state)
  }));

  const paywall = useRef<'purchase' | 'login'>(auth.isAuth ? 'purchase' : 'login');
  const maxViewerCountRef = useRef<number>(0);

  const [state, setState] = useState<EventContainerState>({
    previewMode: false,
    autoplayBlocked: false,
    isConnectionStable: true,
    isForceMuteTooltipVisible: false,
    defaultAudioMuted: router.state.defaultAudioMuted === undefined ? true : router.state.defaultAudioMuted,
    defaultVideoMuted: router.state.defaultVideoMuted === undefined ? true : router.state.defaultVideoMuted,
    isPictureInPictureModeEnabled: false
  });

  const toast = useToast();
  const permissionModal = useModal();
  const becomeMemberModal = useModal();
  const bottomSheet = useBottomSheet<BottomSheetContentKey, BottomSheetParams>();
  const confirmation = useConfirmation<ConfirmationAction>();
  const room = useRoom(eventCode, {
    defaultAudioMuted: state.defaultAudioMuted,
    defaultVideoMuted: state.defaultVideoMuted,
    ingestServer,
    onJoinedToStage: () => {
      isHost && startRTMPStreaming();
    },
    onHandRaiseApproved: () => {
      tracker.getHandRaiseApproval(eventCode);
      enablePreviewMode();
    },
    onInvitedToStage: () => {
      tracker.getInvitedToStage(eventCode);
      enablePreviewMode();
    },
    onRemovedFromStage: () => {
      disablePreviewMode();

      if (inviteeCode && eventSession.username === inviteeCode) {
        router.replace(getEventURL(channelName, eventCode));
      }
    },
    onPermissionDenied: () => permissionModal.open(),
    onMuted: () => showForceMuteTooltip(),
    onEnded: () => onEventEnded?.(),
    onAudienceLimitIsReached,
    onJoiningDeclined: () => {},
    onJoinedFromAnotherSession,
    onNetworkQualityChanged: isStable => setState(prev => ({ ...prev, isConnectionStable: isStable }))
  });

  const gridViewEnabled = room.layout === Layout.GRID_VIEW;

  const isControlPanelVisible = !state.previewMode;

  const gestureCaptured = !room.loading && !state.previewMode && !state.autoplayBlocked;

  const showPlayOverlay = !room.loading && !state.previewMode && state.autoplayBlocked;

  const confirmations = useMemo<Record<string, { title: string; message: string; submitText: string }>>(
    () => ({
      END_EVENT: {
        title: 'Do you want to end the event?',
        message: 'The live event will be ended the event for all?',
        submitText: 'End event'
      },
      LEAVE_STAGE: {
        title: 'Do you want to leave the stage?',
        message: 'Leave the stage and become a viewer',
        submitText: 'Leave stage'
      },
      LEAVE_EVENT: {
        title: 'Do you want to leave the event?',
        message: 'Leave the event quietly',
        submitText: 'Leave event'
      },
      DECLINE_JOINING: {
        title: 'Do you want to decline joining the stage?',
        message: '',
        submitText: 'Decline'
      }
    }),
    []
  );

  useAutoEndWarning({ eventCode, show: isHost });

  // join automatically when coming from setup_panel effect
  useEffect(() => {
    if (router.state.from === 'setup_panel') {
      room
        .joinStage()
        .then(() => room.publishLocalTracks(!router.state.defaultAudioMuted, !router.state.defaultVideoMuted))
        .catch(e => {
          LoggerService.log('JOIN_STAGE', e);
        });
    }
  }, []);

  // fetch invitee token
  useEffect(() => {
    if (isInvitee) {
      fetchInviteeToken();
    }
  }, [isInvitee]);

  // enable preview mode initially by role effect
  useEffect(() => {
    if (isHost && router.state.from !== 'setup_panel') {
      enablePreviewMode();
    } else if (isInvitee || isSpeaker || isModerator || isInvitedToStage) {
      enablePreviewMode();
    }
  }, []);

  // join as a viewer and start event triggers
  useEffect(() => {
    if (auth.isAuth && isAnonymousViewer) {
      api.requests.ltv
        .joinAsViewer(eventCode, { name: auth.name, avatar: auth.image })
        .then(() => {
          startEventTriggers();
        })
        .catch(e => {
          LoggerService.log('JOIN_AS_AUTHENTICATED_VIEWER_THE_LIVE_EVENT', e);
        });
    } else if (auth.isAuth && isViewer) {
      startEventTriggers();
    }
  }, [auth.isAuth, isAnonymousViewer, isViewer]);

  // preview mode handler effect
  useEffect(() => {
    if (state.previewMode) {
      room.preview();
      bottomSheet.open(BottomPanelKey.JOIN_THE_STAGE);
    } else {
      bottomSheet.close();
    }
  }, [state.previewMode]);

  // update maxViewerCountRef
  useEffect(() => {
    if (room.totalViewersCount > maxViewerCountRef.current) {
      maxViewerCountRef.current = room.totalViewersCount;
    }
  }, [room.totalViewersCount]);

  // send request to update max viewer value
  useEffect(() => {
    let intervalId;

    updateMaxViewerCount();

    if (isHost) {
      intervalId = setInterval(() => updateMaxViewerCount(), MAX_VIEWER_COUNT_UPDATE_INTERVAL);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isHost]);

  // show become member modal (annoying modal) for unpaid viewers periodically to urge them to pay
  // NEW: alternate paywall and login panel
  useEffect(() => {
    if (
      // TODO: custom implementation for UzbekITTV
      eventMeta.channelName.toLocaleLowerCase() === UZBEKITTV_CHANNEL_NAME &&
      !isMember &&
      room.isPassiveViewer
    ) {
      const interval = setInterval(() => {
        if (paywall.current === 'purchase') {
          if (!auth.isAuth) {
            paywall.current = 'login';
          }

          becomeMemberModal.open();

          if (document.pictureInPictureElement) disablePictureInPictureMode(true);
        } else if (paywall.current === 'login') {
          paywall.current = 'purchase';
          bottomSheet.open(BottomPanelKey.LOGIN);
        }
      }, SHOW_BECOME_MEMBER_MODAL_INTERVAL);

      return () => clearInterval(interval);
    }

    return () => {};
  }, [isMember, room.isPassiveViewer, auth.isAuth]);

  const fetchInviteeToken = async () => {
    try {
      if (inviteeCode) {
        const {
          data: { response }
        } = await api.requests.ltv.getInviteeToken(eventCode, inviteeCode);

        const [, tokenValue] = response.split(' ');

        if (tokenValue) {
          dispatch(
            eventsActions.updateSession({
              eventCode,
              session: { token: tokenValue, tokenSource: LiveEventTokenSource.INVITEE_CODE }
            })
          );
        }
      }
    } catch (e) {
      LoggerService.log('GET_INVITEE_TOKEN', e);
    }
  };

  const resumeAudio = () => {
    tracker.resumeBlockedAudio(eventCode);

    if (room.isPassiveViewer) {
      // resuming the playback audio is proceeded inside the playback component
      onAutoplayResumed();
    } else {
      room.resumeAudio();
      onAutoplayResumed();
    }
  };

  const enablePreviewMode = () => setState(prev => ({ ...prev, previewMode: true }));

  const disablePreviewMode = () => setState(prev => ({ ...prev, previewMode: false }));

  const showForceMuteTooltip = () => setState(prev => ({ ...prev, isForceMuteTooltipVisible: true }));

  const hideForceMuteTooltip = () => setState(prev => ({ ...prev, isForceMuteTooltipVisible: false }));

  /**
   *
   * @param request - request browser to enable PiP mode
   */
  const enablePictureInPictureMode = async (request: boolean = false) => {
    const prevValue = state.isPictureInPictureModeEnabled;
    const nextValue = true;

    setState(prev => ({ ...prev, isPictureInPictureModeEnabled: nextValue }));

    if (request) {
      try {
        const playbackEl = document.getElementById('live_playback') as HTMLVideoElement | null;

        if (playbackEl) {
          await playbackEl.requestPictureInPicture();
        }
      } catch (e) {
        LoggerService.log('ENABLE_PIP_MODE_ON_LIVE_EVENT', e);
        setState(prev => ({ ...prev, isPictureInPictureModeEnabled: prevValue }));
      }
    }
  };

  /**
   *
   * @param request - request browser to disable PiP mode
   */
  const disablePictureInPictureMode = async (request: boolean = false) => {
    const prevValue = state.isPictureInPictureModeEnabled;
    const nextValue = false;

    setState(prev => ({ ...prev, isPictureInPictureModeEnabled: nextValue }));

    if (request) {
      try {
        await document.exitPictureInPicture();
      } catch (e) {
        LoggerService.log('DISABLE_PIP_MODE_ON_LIVE_EVENT', e);
        setState(prev => ({ ...prev, isPictureInPictureModeEnabled: prevValue }));
      }
    }
  };

  // TODO: get streamKey in server-side
  const startRTMPStreaming = async () => {
    if (callbackTriggers.length) {
      const callbackTriggersMap = getCallbackTriggersMap(callbackTriggers);

      if (callbackTriggersMap[UserActionType.PUSH_STREAM_TO_YOUTUBE]) {
        const pushToYoutube = async (): Promise<void> => {
          const rtmpUrl = await generateStreamingEndpoint({
            platform: StreamingPlatform.YOUTUBE,
            eventName
          });

          await room.pushStreamToCDN(rtmpUrl);

          dispatch(
            streamingPlatformsActions.updateStreamingPlatform({
              platformKey: StreamingPlatform.YOUTUBE,
              platformState: { url: rtmpUrl, stopped: false }
            })
          );
        };

        toast
          .promise(
            pushToYoutube(),
            {
              loading: 'Please wait, you will be live on Youtube shortly',
              success: 'You are live on Youtube!',
              error: 'Youtube stream failed to start'
            },
            { id: 'START_LIVE_STREAM_ON_YOUTUBE' }
          )
          .then(() => {
            dispatch(callbackTriggersActions.remove(UserActionType.PUSH_STREAM_TO_YOUTUBE));
          })
          .catch(e => {
            LoggerService.log('PUSH_TO_YOUTUBE_TRIGGER', e);
          });
      }

      if (callbackTriggersMap[UserActionType.PUSH_STREAM_TO_FACEBOOK]) {
        const pushToFacebook = async (): Promise<void> => {
          // createBroadcast
          const facebookPlatform = activeStreamingPlatforms.find(item => item.key === StreamingPlatform.FACEBOOK) as
            | FacebookStreamingPlatformState
            | undefined;

          if (facebookPlatform) {
            const rtmpUrl = await generateStreamingEndpoint({
              platform: StreamingPlatform.FACEBOOK,
              eventName,
              state: facebookPlatform.state
            });

            await room.pushStreamToCDN(rtmpUrl);

            dispatch(
              streamingPlatformsActions.updateStreamingPlatform({
                platformKey: StreamingPlatform.FACEBOOK,
                platformState: { url: rtmpUrl, stopped: false }
              })
            );
          }
        };

        toast
          .promise(
            pushToFacebook(),
            {
              loading: 'Please wait, you will be live on Facebook soon...',
              success: 'You are live on Facebook!',
              error: 'Facebook stream failed to start'
            },
            { id: 'START_LIVE_STREAM_ON_FACEBOOK' }
          )
          .then(() => {
            dispatch(callbackTriggersActions.remove(UserActionType.PUSH_STREAM_TO_FACEBOOK));
          })
          .catch(e => {
            LoggerService.log('PUSH_STREAM_TO_FACEBOOK', e);
          });
      }

      if (callbackTriggersMap[UserActionType.PUSH_STREAM_TO_TWITCH]) {
        const pushToTwitch = async (): Promise<void> => {
          const rtmpUrl = await generateStreamingEndpoint({ platform: StreamingPlatform.TWITCH, eventName });

          await room.pushStreamToCDN(rtmpUrl);

          dispatch(
            streamingPlatformsActions.updateStreamingPlatform({
              platformKey: StreamingPlatform.TWITCH,
              platformState: { url: rtmpUrl, stopped: false }
            })
          );
        };

        toast
          .promise(
            pushToTwitch(),
            {
              loading: 'Please wait, you will be live on Twitch soon...',
              success: 'You are live on Twitch!',
              error: 'Twitch stream failed to start'
            },
            { id: 'START_LIVE_STREAM_ON_TWITCH' }
          )
          .then(() => {
            dispatch(callbackTriggersActions.remove(UserActionType.PUSH_STREAM_TO_TWITCH));
          })
          .catch(e => {
            LoggerService.log('PUSH_STREAM_TO_TWITCH', e);
          });
      }
    }
  };

  const startEventTriggers = async () => {
    if (callbackTriggers.length) {
      const callbackTriggersMap = getCallbackTriggersMap(callbackTriggers);

      if (callbackTriggersMap[UserActionType.RAISE_HAND]) {
        if (localUser) {
          toast
            .promise(
              room.raiseHand(),
              {
                loading: 'Please wait',
                success: 'You have been added to the waitlist. Moderators will invite you to the stage shortly.',
                error: 'Raise hand request is failed'
              },
              { id: 'HAND_RAISE_REQUEST' }
            )
            .then(() => {
              dispatch(callbackTriggersActions.remove(UserActionType.RAISE_HAND));
            })
            .catch(e => {
              LoggerService.log('RAISE_HAND_TRIGGER', e);
            });
        }
      }
    }
  };

  // used in backstage panel
  const handleOnJoin = async (defaultAudioMuted: boolean, defaultVideoMuted: boolean) => {
    if (isInvitee) {
      const { headers, data: updatedEventUser } = await api.requests.ltv.joinAsInvitee(eventCode, localUser.username);
      const [, tokenValue] = headers.authorization.split(' ');

      if (tokenValue) {
        dispatch(
          eventsActions.updateSession({
            eventCode,
            session: { token: tokenValue, tokenSource: LiveEventTokenSource.INVITEE_CODE }
          })
        );
      }

      dispatch(eventsActions.updateState(updatedEventUser));
    } else if (isInvitedToStage) {
      const { data: updatedEventUser } = await api.requests.ltv.joinAsSpeaker(eventCode);

      dispatch(eventsActions.updateState(updatedEventUser));
    } else if (isHost) {
      // do nothing
    } else if (isSpeaker || isModerator) {
      // do nothing
    }

    await room.publishLocalTracks(!defaultAudioMuted, !defaultVideoMuted);
    room.joinStage();
    disablePreviewMode();
  };

  const handleOnPaywallAttempted = () => {
    dispatch(
      callbackTriggersActions.set([
        { type: UserActionType.PAYME_ATTEMPTED },
        { type: UserActionType.REDIRECT, payload: { path: router.pathname } }
      ])
    );

    if (becomeMemberModal.isOpen) {
      becomeMemberModal.close();
    }

    bottomSheet.open(BottomPanelKey.LOGIN);
  };

  const onDropHand = async () => {
    tracker.dropHand(eventCode, !!localUser);

    await toast.promise(
      room.dropHand(),
      {
        loading: 'Please wait',
        success: 'You have been removed from the waitlist',
        error: 'Drop hand request is failed'
      },
      { id: 'HAND_RAISE_REQUEST' }
    );
  };

  const onRaiseHand = async () => {
    tracker.raiseHand(eventCode, !!localUser);

    await toast.promise(
      room.raiseHand(),
      {
        loading: 'Please wait',
        success: 'You have been added to the waitlist. Moderators will invite you to the stage shortly.',
        error: 'Raise hand request is failed'
      },
      { id: 'HAND_RAISE_REQUEST' }
    );
  };

  const handleOnLeaveStage = async () => {
    confirmation.open('LEAVE_STAGE');
  };

  const handleOnDeclineJoining = () => {
    confirmation.open('DECLINE_JOINING');
  };

  const handleOnEndEvent = async () => {
    if (getDuration(eventMeta.startDate) < PREVENT_ENDING_EVENT_IN) {
      toast.warn('Please, wait a minute and try again', {
        id: 'PREVENT_ENDING_EVENT',
        autoDismiss: true
      });

      return;
    }

    confirmation.open('END_EVENT');
  };

  const onOpenLoginPanel = (attempt?: LiveEventAction) => {
    bottomSheet.open(BottomPanelKey.LOGIN, { reversible: true, params: { attempt } });
  };

  const onOpenRequestsPanel = () => bottomSheet.open(BottomPanelKey.REQUESTS, { reversible: true });

  const onOpenInviteToStage = (invitee?: InviteeSchema, seatIndex?: number) => {
    bottomSheet.open(BottomPanelKey.GENERATE_INVITATION_LINK, {
      params: { defaultRole: UserRole.SPEAKER, invitee, selectedSeatIndex: seatIndex }
    });
  };

  const onOpenGuests = () => {
    tracker.openStudioPanel(eventCode);

    bottomSheet.open(BottomPanelKey.STUDIO, { reversible: true });
  };

  const onAddMember = (memberRole: UserRole.MODERATOR | UserRole.SPEAKER, seatIndex?: number) => {
    bottomSheet.open(BottomPanelKey.GENERATE_INVITATION_LINK, {
      params: { defaultRole: memberRole, selectedSeatIndex: seatIndex }
    });
  };

  const onOpenSharePanel = () => bottomSheet.open(BottomPanelKey.SHARE, { reversible: true });

  const onCloseBottomSheet = () => {
    switch (bottomSheet.state.contentKey) {
      case BottomPanelKey.LOGIN: {
        if (bottomSheet.state.params?.attempt === LiveEventAction.RAISE_HAND) {
          toast.info('Provide your name to join the waitlist. Click the raise-hand icon and log in.', {
            id: 'HAND_RAISE_REQUEST'
          });
        }

        bottomSheet.close();
        break;
      }

      case BottomPanelKey.JOIN_THE_STAGE: {
        handleOnDeclineJoining();
        break;
      }

      default: {
        bottomSheet.close();
      }
    }
  };

  const onAuthorize = (attempt?: LiveEventAction) => {
    dispatch(
      callbackTriggersActions.set([
        {
          type: UserActionType.REDIRECT,
          payload: { path: router.pathname }
        }
      ])
    );

    switch (attempt) {
      case LiveEventAction.RAISE_HAND: {
        dispatch(callbackTriggersActions.add([{ type: UserActionType.RAISE_HAND }]));
        break;
      }
    }
  };

  const handleOnCancelConfirmation = async (action: ConfirmationAction) => {
    switch (action) {
      default: {
        confirmation.close();
      }
    }
  };

  const handleOnSubmitConfirmation = async (action: ConfirmationAction) => {
    switch (action) {
      case 'END_EVENT': {
        updateMaxViewerCount();
        try {
          await room.end();
          router.replace(`/stats/${eventCode}`);
          confirmation.close();
        } catch (e) {
          LoggerService.log('END_THE_LIVE_EVENT', e);
          if (e instanceof Error) {
            toast.error(e.message, { id: 'END_THE_LIVE_EVENT' });
          }
        }

        break;
      }

      case 'LEAVE_STAGE': {
        const withInviteeCode = !!inviteeCode;

        try {
          await room.leaveStage(withInviteeCode);
          if (state.previewMode) disablePreviewMode();
          if (withInviteeCode) router.replace(getEventURL(channelName, eventCode));
          confirmation.close();
        } catch (e) {
          LoggerService.log('LEAVE_STAGE', e);
          if (e instanceof Error) {
            toast.error(e.message, { id: 'LEAVE_STAGE' });
          }
        }

        break;
      }

      case 'LEAVE_EVENT': {
        if (auth.isAuth) {
          try {
            await room.leaveEvent();
            router.replace(getChannelURL(channelName), {
              eventCode,
              reason: EventLeaveReason.EXIT
            });
            confirmation.close();
          } catch (e) {
            LoggerService.log('LEAVE_EVENT', e);
            if (e instanceof Error) {
              toast.error(e.message, { id: 'LEAVE_EVENT' });
            }
          }
        } else {
          router.replace(getChannelURL(channelName), {
            eventCode,
            reason: EventLeaveReason.EXIT
          });
          confirmation.close();
        }

        break;
      }

      case 'DECLINE_JOINING': {
        const withInviteeCode = !!inviteeCode;

        try {
          await room.declineJoining(isInvitee);
          disablePreviewMode();
          if (withInviteeCode) {
            router.replace(getEventURL(channelName, eventCode));
          }
          confirmation.close();
        } catch (e) {
          LoggerService.log('DECLINE_TO_JOIN_THE_STAGE', e);
          if (e instanceof Error) {
            toast.error(e.message, { id: 'DECLINE_TO_JOIN_THE_STAGE' });
          }
        }
      }
    }
  };

  const handleOnLeaveEvent = () => {
    tracker.exitEvent(eventCode);
    confirmation.open('LEAVE_EVENT');
  };

  const onRemoveFromStage = ({ type, username }: OnStageUser) => room.removeFromStage(username, type);

  const onInviteViewerToStage = (username: string) => {
    toast.promise(
      room.inviteToStage(username),
      {
        loading: 'Please wait',
        success: 'Invited',
        error: 'Invite request is failed'
      },
      { id: `INVITE_TO_STAGE:${username}` }
    );
  };

  const onApproveHandRaise = async (username: string) => {
    await toast.promise(
      room.approveHandRaise(username),
      {
        loading: 'Please wait',
        success: 'Invited',
        error: 'Approve request is failed'
      },
      { id: `APPROVE_HAND_RAISE:${username}` }
    );
  };

  const onRejectHandRaise = async (username: string) => {
    await toast.promise(
      room.rejectHandRaise(username),
      {
        loading: 'Please wait',
        success: 'Removed',
        error: 'Reject request is failed'
      },
      { id: `REJECT_HAND_RAISE:${username}` }
    );
  };

  const onEnableHandRaise = async () => {
    tracker.enableHandRaise(eventCode);

    const prevValue = false;
    const newValue = true;

    try {
      dispatch(eventsActions.toggleHandRaisePermission({ eventCode, enabled: newValue }));
      const { data: updatedEventState } = await api.requests.ltv.toggleHandRaisePermission({
        eventCode,
        enable: newValue
      });

      tracker.enableHandRaiseSuccess(eventCode);
      dispatch(eventsActions.updateState(updatedEventState));
      room.enableHandRaise();
    } catch (e) {
      LoggerService.log('ENABLE_HAND_RAISE_PERMISSION', e);
      dispatch(eventsActions.toggleHandRaisePermission({ eventCode, enabled: prevValue }));
      toast.error('Enabling the hand raise is failed', { id: 'TOGGLE_HAND_RAISE_PERMISSION' });
      if (e instanceof Error) {
        tracker.enableHandRaiseFail(eventCode, e);
      }
    }
  };

  const onDisableHandRaise = async () => {
    tracker.disableHandRaise(eventCode);

    const prevValue = true;
    const newValue = false;

    try {
      dispatch(eventsActions.toggleHandRaisePermission({ eventCode, enabled: newValue }));
      const { data: updatedEventState } = await api.requests.ltv.toggleHandRaisePermission({
        eventCode,
        enable: newValue
      });

      tracker.disableHandRaiseSuccess(eventCode);
      dispatch(eventsActions.updateState(updatedEventState));
      room.disableHandRaise();
    } catch (e) {
      LoggerService.log('DISABLE_HAND_RAISE_PERMISSION', e);
      dispatch(eventsActions.toggleHandRaisePermission({ eventCode, enabled: prevValue }));
      toast.error('Disabling the hand raise is failed', { id: 'TOGGLE_HAND_RAISE_PERMISSION' });
      if (e instanceof Error) {
        tracker.disableHandRaiseFail(eventCode, e);
      }
    }
  };

  const onForceMuteSpeaker = async ({ username }: OnStageUser) => {
    toast.promise(
      room.forceMute(username),
      {
        loading: 'Please wait',
        success: 'Muted',
        error: 'Mute request is failed'
      },
      { id: `FORCE_MUTE:${username}` }
    );
  };

  const onSwitchRole = ({ username }: OnStageUser, promote: boolean) => {
    room.switchRole(username, promote);
  };

  const onAutoplayBlocked = () => setState(prev => ({ ...prev, autoplayBlocked: true }));

  const onAutoplayResumed = () => setState(prev => ({ ...prev, autoplayBlocked: false }));

  const onGenerateInvitationLink = async (name: string, role: UserRole.MODERATOR | UserRole.SPEAKER) => {
    if (filledSeatsCount >= MAX_STAGE_SEATS) {
      toast.error('No seats available', { id: 'NO_SEATS_AVAILABLE' });
      bottomSheet.close();
      return null;
    }

    const res = await toast.promise(
      room.generateInvitationLink(name, role),
      {
        loading: 'Please wait',
        error: 'Failed to generate'
      },
      { id: `GENERATE_INVITATION_LINK` }
    );

    return res;
  };

  const onResetInvitationLink = (_inviteeCode: string) => room.resetInvitationLink(_inviteeCode);

  const updateMaxViewerCount = async () => {
    try {
      if (!isHost) {
        return;
      }

      const {
        data: { maxViewerCount }
      } = await api.requests.ltv.updateMaxViewerCount(eventCode, maxViewerCountRef.current);

      maxViewerCountRef.current = maxViewerCount;
    } catch (error) {
      LoggerService.log('UPDATE_MAX_VIEWER_COUNT', error);
    }
  };

  const changeLayout = () => room.changeLayout(gridViewEnabled ? Layout.ACTIVE_SPEAKER : Layout.GRID_VIEW, true);

  const renderPinButton = () => {
    if (!room.pinIsAvailable || gridViewEnabled) return null;

    return (
      <Pin
        currentActiveSpeakerUsername={room.activeSpeaker?.username || null}
        pinned={room.isPinned}
        onPin={room.pinActiveSpeaker}
      />
    );
  };

  const renderWarningMessage = () => {
    if (!hasFullSupport()) {
      return (
        <div className={styles.unsupported}>
          <Unsupported />
        </div>
      );
    }

    return null;
  };

  const renderPlayback = () => {
    if (playbackUrl) {
      return (
        <Playback
          isRoomLoading={room.loading}
          active={room.isPassiveViewer}
          playbackUrl={playbackUrl}
          gestureCaptured={gestureCaptured}
          isPassiveViewer={room.isPassiveViewer}
          onAutoplayBlocked={onAutoplayBlocked}
        />
      );
    }

    return null;
  };

  return (
    <liveEventContext.Provider
      value={{
        channelName,
        eventCode,
        inviteeCode,
        isPictureInPictureModeEnabled: state.isPictureInPictureModeEnabled,
        enablePictureInPictureMode,
        disablePictureInPictureMode,
        openLoginPanel: onOpenLoginPanel,
        closePanel: onCloseBottomSheet
      }}
    >
      <div className={styles.wrapper}>
        {renderWarningMessage()}
        {room.loading && !state.previewMode && <Loader />}
        {permissionModal.isOpen && <PermissionModal onClose={permissionModal.close} />}
        <BecomeMemberModal
          channelName={channelName}
          eventType={EventType.LIVE_EVENT}
          eventCode={eventCode}
          closable
          isOpen={becomeMemberModal.isOpen}
          onClose={becomeMemberModal.close}
          onAuthRequired={handleOnPaywallAttempted}
        />

        <div className={styles.container}>
          {renderPlayback()}

          {!gridViewEnabled && !room.isPassiveViewer && (
            <MainPlayer image={room.activeSpeaker?.image} name={room.activeSpeaker?.name} />
          )}

          <div className={styles.content}>
            {showPlayOverlay && <PlayAudio onClick={resumeAudio} />}
            <TopBar
              isConnectionStable={state.isConnectionStable}
              isRecordingEnabled={isRecorded}
              cameraSwitchDisabled={room.videoMuted}
              onToggleFacingMode={room.switchCamera}
              onLeave={handleOnLeaveStage}
              onEnd={handleOnEndEvent}
              onExit={handleOnLeaveEvent}
            />

            {!room.isPassiveViewer && (
              <>
                <div className={styles.floatingButtons}>{renderPinButton()}</div>

                <OnStage
                  layout={room.layout}
                  users={room.stageUsers}
                  facingMode={room.facingMode}
                  activeSpeakerUsername={room.activeSpeaker?.username}
                  activeSpeakerName={room.activeSpeaker?.name}
                  activeSpeakerVideoMuted={room.activeSpeaker?.videoMuted}
                  onSelectActiveSpeaker={room.changeActiveSpeaker}
                  onOpenInviteToStage={onOpenInviteToStage}
                />
              </>
            )}
          </div>

          {isControlPanelVisible && (
            <div className={styles.controlPanel}>
              <div className="container">
                <ControlPanel
                  gestureCaptured={gestureCaptured}
                  usersCount={room.membersCount}
                  requestsCount={room.requestsCount}
                  audioMuted={room.audioMuted}
                  videoMuted={room.videoMuted}
                  handRaised={room.handRaised}
                  isForceMuteTooltipVisible={state.isForceMuteTooltipVisible}
                  isHandRaiseEnabled={isHandRaiseEnabled}
                  isPassiveViewer={room.isPassiveViewer}
                  isLayoutSwitchAvailable={!room.isPassiveViewer && room.isGridLayoutAvailable}
                  gridViewEnabled={gridViewEnabled}
                  onToggleAudio={room.toggleAudio}
                  onToggleVideo={room.toggleVideo}
                  onOpenGuests={onOpenGuests}
                  onRaiseHand={onRaiseHand}
                  onDropHand={onDropHand}
                  onOpenRequests={onOpenRequestsPanel}
                  onOpenShare={onOpenSharePanel}
                  onHideForceMuteTooltip={hideForceMuteTooltip}
                  onChangeLayout={changeLayout}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <BottomPanels
        panels={[
          {
            name: BottomPanelKey.JOIN_THE_STAGE,
            show: bottomSheet.state.contentKey === BottomPanelKey.JOIN_THE_STAGE,
            closeOnClickBackdrop: false,
            title: 'Join the stage',
            renderHeader: ({ headerClasses, titleClasses, closeButton, title }) => (
              <div className={headerClasses}>
                <div className={titleClasses}>{title}</div>
                {!isHost && closeButton}
              </div>
            ),
            component: <JoinPanel isReady={room.ready} onJoined={handleOnJoin} />
          },
          {
            name: BottomPanelKey.STUDIO,
            show: bottomSheet.state.contentKey === BottomPanelKey.STUDIO,
            title: 'Guests',
            component: (
              <Guests
                stageUsers={room.stageUsers}
                viewers={room.viewers}
                totalViewersCount={room.totalViewersCount}
                anonymousViewersCount={room.anonymousViewersCount}
                onShare={onOpenSharePanel}
                onAddMember={onAddMember}
                onSwitchRole={onSwitchRole}
                onRemoveFromStage={onRemoveFromStage}
                onInviteViewerToStage={onInviteViewerToStage}
                onMute={onForceMuteSpeaker}
              />
            )
          },
          {
            enabled:
              liveEventActions.hasPermission(LiveEventAction.TOGGLE_HAND_RAISE_PERMISSION) ||
              liveEventActions.hasPermission(LiveEventAction.REJECT_HAND_RAISE_REQUEST) ||
              liveEventActions.hasPermission(LiveEventAction.APPROVE_HAND_RAISE_REQUEST),
            name: BottomPanelKey.REQUESTS,
            show: bottomSheet.state.contentKey === BottomPanelKey.REQUESTS,
            title: 'Hand raises',
            renderHeader: ({ closeButton, headerClasses, titleClasses, title }) => (
              <div className={headerClasses}>
                <div className={titleClasses}>
                  <RaiseHandIcon height={18} width={18} className="mr-2" />
                  <span>{title}</span>
                </div>
                {closeButton}
              </div>
            ),
            component: (
              <HandRaises
                requests={room.requests}
                onApprove={onApproveHandRaise}
                onReject={onRejectHandRaise}
                onEnable={onEnableHandRaise}
                onDisable={onDisableHandRaise}
              />
            )
          },
          {
            enabled: liveEventActions.hasPermission(LiveEventAction.GENERATE_INVITATION_LINK),
            name: BottomPanelKey.GENERATE_INVITATION_LINK,
            show: bottomSheet.state.contentKey === BottomPanelKey.GENERATE_INVITATION_LINK,
            title: 'Invite to stage',
            component: (
              <InviteToStage
                defaultInvitee={bottomSheet.state.params?.invitee}
                defaultRole={
                  bottomSheet.state.params?.defaultRole === UserRole.MODERATOR ? UserRole.MODERATOR : UserRole.SPEAKER
                }
                onGenerate={onGenerateInvitationLink}
                onReset={onResetInvitationLink}
              />
            )
          },
          {
            name: BottomPanelKey.SHARE,
            show: bottomSheet.state.contentKey === BottomPanelKey.SHARE,
            title: 'Share with friends',
            component: <SharePanel inviteeCode={inviteeCode} />
          },
          {
            enabled: !auth.isAuth,
            name: BottomPanelKey.LOGIN,
            show: bottomSheet.state.contentKey === BottomPanelKey.LOGIN,
            title: 'Log in',
            component: <Login onAuthorize={() => onAuthorize(bottomSheet.state.params?.attempt)} />
          }
        ]}
        onClose={onCloseBottomSheet}
      />

      {Object.keys(confirmations).map(name => (
        <ConfirmPanel
          key={name}
          show={confirmation.state.isOpen && confirmation.state.name === name}
          name={name}
          title={confirmations[name].title}
          message={confirmations[name].message}
          submitText={confirmations[name].submitText}
          onCancel={handleOnCancelConfirmation}
          onSubmit={handleOnSubmitConfirmation}
        />
      ))}
    </liveEventContext.Provider>
  );
};

export default LiveEvent;
